import { useState, useEffect } from "react";
import { message } from "antd";
import { useUser } from "../../../context/UserContext";
import {
  getGatewaySystems, getGatewayTemplates, getGatewayFields,
  getGatewaySftp, getSavedMappings, uploadSpec,
} from "../../../service/gateway.service";

/**
 * Manages system/template selection, spec upload and field preview state.
 */
export function useGatewaySystem() {
  const { email } = useUser();

  const [loading,          setLoading]          = useState(true);
  const [systems,          setSystems]          = useState([]);
  const [selectedSystem,   setSelectedSystem]   = useState(null);
  const [templates,        setTemplates]        = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [fields,           setFields]           = useState([]);
  const [sftpDests,        setSftpDests]        = useState([]);
  const [savedMappings,    setSavedMappings]    = useState([]);
  const [previewOpen,      setPreviewOpen]      = useState(false);
  const [previewTemplate,  setPreviewTemplate]  = useState(null);
  const [previewFields,    setPreviewFields]    = useState([]);

  useEffect(() => {
    if (!email) return;
    getGatewaySystems(email)
      .then((r) => { setSystems(r.data || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [email]);

  const onSelectSystem = (sys) => {
    setSelectedSystem(sys);
    setSelectedTemplate(null);
    setFields([]);
    setLoadingTemplates(true);
    getGatewayTemplates(sys.systemId)
      .then((r) => { setTemplates(r.data || []); setLoadingTemplates(false); })
      .catch(() => setLoadingTemplates(false));
    getGatewaySftp(sys.systemId).then((r) => setSftpDests(r.data || []));
  };

  const onSelectTemplate = (t, sourceColumns, autoSuggest) => {
    setSelectedTemplate(t);
    getGatewayFields(t.templateId).then((r) => {
      setFields(r.data || []);
      if (sourceColumns?.length > 0) autoSuggest?.(r.data || [], sourceColumns);
    });
    getSavedMappings(t.templateId, email).then((r) => setSavedMappings(r.data || []));
  };

  const onPreviewTemplate = (t) => {
    setPreviewTemplate(t);
    getGatewayFields(t.templateId).then((r) => {
      setPreviewFields(r.data || []);
      setPreviewOpen(true);
    });
  };

  const onSpecUploaded = async (file, onTemplateSelected) => {
    try {
      const res = await uploadSpec(file, selectedSystem.systemId, email);
      message.success(`Spec uploaded — ${res.data.fieldCount} fields parsed`);
      setLoadingTemplates(true);
      const tr = await getGatewayTemplates(selectedSystem.systemId);
      setTemplates(tr.data || []);
      setLoadingTemplates(false);
      const newT = (tr.data || []).find((t) => t.templateId === res.data.templateId);
      if (newT) onTemplateSelected(newT);
    } catch (e) {
      message.error("Spec upload failed: " + (e.response?.data?.error || e.message));
    }
  };

  return {
    email, loading, systems,
    selectedSystem, setSelectedSystem,
    templates, loadingTemplates,
    selectedTemplate, setSelectedTemplate,
    fields, setFields,
    sftpDests, savedMappings,
    previewOpen, setPreviewOpen,
    previewTemplate, previewFields,
    onSelectSystem, onSelectTemplate, onPreviewTemplate, onSpecUploaded,
  };
}
